import React, { useState, useEffect, useRef } from "react";
import { Phone, Mail, MapPin, Globe, Loader2 } from "lucide-react";

const Contact = () => {
  const formRef = useRef();

  const [status, setStatus] = useState({
    loading: false,
    success: null,
    error: null,
  });
  const [sdkLoaded, setSdkLoaded] = useState(false);

  const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

  useEffect(() => {
    if (window.emailjs) {
      window.emailjs.init(PUBLIC_KEY);
      setSdkLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/emailjs-com@3/dist/email.min.js";
    script.async = true;
    script.onload = () => {
      window.emailjs.init(PUBLIC_KEY);
      setSdkLoaded(true);
    };
    document.body.appendChild(script);
  }, [PUBLIC_KEY]);

  const sendEmail = async (e) => {
    e.preventDefault();

    // Debug logging
    console.log("🔍 Debug Info:");
    console.log("SDK Loaded:", sdkLoaded);
    console.log("SERVICE_ID:", SERVICE_ID ? "✅ Set" : "❌ Missing");
    console.log("TEMPLATE_ID:", TEMPLATE_ID ? "✅ Set" : "❌ Missing");
    console.log("PUBLIC_KEY:", PUBLIC_KEY ? "✅ Set" : "❌ Missing");

    if (!sdkLoaded || typeof window.emailjs === "undefined") {
      console.error("EmailJS SDK not loaded.");
      setStatus({
        loading: false,
        success: null,
        error: "Sending service not ready. Please try again.",
      });
      return;
    }

    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      console.error("❌ EmailJS environment variables are not loaded.");
      setStatus({
        loading: false,
        success: null,
        error: "Configuration error. Check console.",
      });
      return;
    }

    setStatus({ loading: true, success: null, error: null });

    try {
      const result = await window.emailjs.sendForm(
        SERVICE_ID,
        TEMPLATE_ID,
        formRef.current,
        PUBLIC_KEY
      );
      console.log("✅ Email sent successfully:", result);
      setStatus({
        loading: false,
        success: "Message sent successfully ✅",
        error: null,
      });
      formRef.current.reset();
      setTimeout(
        () => setStatus({ loading: false, success: null, error: null }),
        5000
      );
    } catch (err) {
      console.error("❌ EmailJS error:", err);
      console.error("Error details:", err.text || err.message);
      setStatus({
        loading: false,
        success: null,
        error: `Failed: ${err.text || "Unknown error"}`,
      });
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            Get In Touch
          </h2>
          <p className="text-xl text-gray-600">
            Ready to start your e-commerce journey? Contact us today!
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="flex items-center space-x-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl hover:shadow-lg transition-all border border-green-100">
              <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <Phone className="text-white" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-lg">Phone</h3>
                <p className="text-green-600 font-semibold text-lg">
                  9319201205
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl hover:shadow-lg transition-all border border-green-100">
              <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <Mail className="text-white" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-lg">Email</h3>
                <p className="text-green-600 font-semibold">
                  venusecomcare@gmail.com
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl hover:shadow-lg transition-all border border-green-100">
              <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <Globe className="text-white" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-lg">Website</h3>
                <a
                  href="https://www.venusecomecare.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 font-semibold hover:text-green-700 transition-colors"
                >
                  www.venusecomecare.in
                </a>
              </div>
            </div>

            <div className="flex items-start space-x-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl hover:shadow-lg transition-all border border-green-100">
              <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0 mt-1">
                <MapPin className="text-white" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-lg">Address</h3>
                <p className="text-gray-600 leading-relaxed">
                  Sector-15 Dwarka
                  <br />
                  New Delhi - 110078 <br />
                </p>
                <a
                  href="https://share.google/1Xa42fLkNDiq9IFdE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-1 bg-green-600 text-white px-4 py-2 rounded-xl font-semibold text-sm hover:bg-green-700 transition-all shadow-md"
                >
                  View on Google Maps
                </a>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl p-8 text-white shadow-2xl">
            <h3 className="text-2xl font-bold mb-8 text-center">
              Send us a message
            </h3>

            <form ref={formRef} className="space-y-6" onSubmit={sendEmail}>
              {/* Hidden field for title - matches {{title}} in template */}
              <input type="hidden" name="title" value="Contact Us" />

              {/* Hidden field for time - matches {{time}} in template */}
              <input
                type="hidden"
                name="time"
                value={new Date().toLocaleString()}
              />

              {/* Name field - matches {{name}} */}
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                required
                className="w-full px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
              />

              {/* Email field - matches {{email}} */}
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                required
                className="w-full px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
              />

              {/* Message field - matches {{message}} */}
              <textarea
                name="message"
                placeholder="Your Message"
                rows="4"
                required
                className="w-full px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50 resize-none"
              />

              <button
                type="submit"
                disabled={status.loading || !sdkLoaded}
                className="w-full bg-white text-green-600 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg flex items-center justify-center disabled:opacity-70"
              >
                {status.loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Message"
                )}
              </button>

              <div className="h-5 text-center">
                {!sdkLoaded && !status.error && (
                  <p className="text-sm text-yellow-200">
                    Initializing form...
                  </p>
                )}
                {status.success && (
                  <p className="text-sm text-green-200">{status.success}</p>
                )}
                {status.error && (
                  <p className="text-sm text-red-200">{status.error}</p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
